<template>
  <section class="waajo-renewal" :aria-busy="String(loading)">
    <header class="waajo-renewal-head">
      <div class="waajo-renewal-heading">
        <span class="waajo-renewal-icon" aria-hidden="true">
          <i class="fa fa-clock-o"></i>
        </span>
        <div>
          <h2>Perpanjang Lisensi</h2>
          <p>
            Lisensi yang berakhir ditampilkan hingga 15 hari setelah masa
            aktifnya habis.
          </p>
        </div>
      </div>
      <router-link to="/license/list" class="waajo-renewal-all">
        Lihat Semua Lisensi
        <i class="fa fa-arrow-right" aria-hidden="true"></i>
      </router-link>
    </header>

    <div v-if="loading" class="waajo-renewal-loading" role="status">
      <div class="waajo-renewal-skeleton" v-for="index in 2" :key="index">
        <span class="waajo-skeleton-circle"></span>
        <span class="waajo-skeleton-lines">
          <span></span>
          <span></span>
        </span>
        <span class="waajo-skeleton-button"></span>
      </div>
    </div>

    <div v-else-if="errorMessage" class="waajo-renewal-error" role="alert">
      <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
      <span>{{ errorMessage }}</span>
      <button type="button" class="waajo-renewal-retry" @click="loadLicenses">
        Coba lagi
      </button>
    </div>

    <div v-else-if="items.length" class="waajo-renewal-list">
      <div
        class="waajo-renewal-row"
        :class="'is-' + item.expiry.state"
        v-for="item in items"
        :key="item.id"
      >
        <span class="waajo-renewal-wa" aria-hidden="true">
          <i class="fa fa-whatsapp"></i>
        </span>

        <div class="waajo-renewal-info">
          <strong>
            {{ item.license_phone_number || "-" }}
            <span v-if="item.isActive" class="waajo-renewal-active-tag">
              Aktif
            </span>
          </strong>
          <span>{{ item.license_name || "Lisensi WhatsApp" }}</span>
        </div>

        <div class="waajo-renewal-status">
          <span class="waajo-renewal-badge">
            <i
              :class="
                item.expiry.state === 'warning'
                  ? 'fa fa-exclamation-triangle'
                  : 'fa fa-exclamation-circle'
              "
              aria-hidden="true"
            ></i>
            {{ item.statusText }}
          </span>
          <small>{{ item.statusDetail }}</small>
        </div>

        <div class="waajo-renewal-remaining">
          <strong>{{ formatLicenseDayLabel(item.expiry) }}</strong>
          <span>Masa aktif hingga {{ item.expiry.dateLabel }}</span>
        </div>

        <router-link
          :to="renewalRoute(item)"
          class="waajo-renewal-action"
          :class="'is-' + item.expiry.state"
        >
          {{ item.expiry.state === "warning" ? "Pilih Paket" : "Perpanjang" }}
          <i class="fa fa-arrow-right" aria-hidden="true"></i>
        </router-link>
      </div>
    </div>

    <div v-else class="waajo-renewal-safe">
      <span class="waajo-renewal-safe-icon" aria-hidden="true">
        <i class="fa fa-check"></i>
      </span>
      <span>Semua lisensi dalam kondisi aman.</span>
    </div>

    <p v-if="!loading && !errorMessage && unavailableCount > 0" class="waajo-renewal-note">
      {{ unavailableCount }} lisensi tidak memiliki tanggal masa aktif yang
      valid sehingga tidak ditampilkan pada pengingat ini.
    </p>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import {
  getLicenseExpiryInfo,
  formatLicenseDayLabel
} from "@/utils/licenseExpiry";
import { getApiErrorMessage } from "@/utils/redesignErrors";

export default {
  name: "license-renewal",
  data() {
    return {
      loading: true,
      errorMessage: "",
      licenses: []
    };
  },
  computed: {
    ...mapGetters(["agentSession"]),
    items() {
      const activeId = this.agentSession ? this.agentSession.license_id : "";
      const rows = [];

      this.licenses.forEach(license => {
        if (Number(license.is_unlimited) === 1 || !license.id) {
          return;
        }

        const expiry = getLicenseExpiryInfo(license.license_masa_aktif);
        if (expiry.state !== "warning" && expiry.state !== "expired") {
          return;
        }

        const isWarning = expiry.state === "warning";
        rows.push({
          ...license,
          expiry: expiry,
          isActive: String(license.id) === String(activeId),
          statusText: isWarning
            ? expiry.days === 0
              ? "Berakhir hari ini"
              : "Akan berakhir dalam " + expiry.days + " hari"
            : "Sudah kedaluwarsa",
          statusDetail: isWarning
            ? "Masa aktif hingga " + expiry.dateLabel
            : "Berakhir " + expiry.dateLabel
        });
      });

      return rows.sort(function(a, b) {
        const aGroup = a.expiry.state === "warning" ? 0 : 1;
        const bGroup = b.expiry.state === "warning" ? 0 : 1;
        if (aGroup !== bGroup) {
          return aGroup - bGroup;
        }
        return a.expiry.date.getTime() - b.expiry.date.getTime();
      });
    },
    unavailableCount() {
      return this.licenses.filter(function(license) {
        if (Number(license.is_unlimited) === 1) {
          return false;
        }
        return getLicenseExpiryInfo(license.license_masa_aktif).state === "unknown";
      }).length;
    }
  },
  created() {
    this.loadLicenses();
  },
  methods: {
    formatLicenseDayLabel(info) {
      return formatLicenseDayLabel(info);
    },
    renewalRoute(license) {
      return {
        name: "subscriptions",
        query: {
          license_id: String(license.id)
        }
      };
    },
    async loadLicenses() {
      if (this.loading && this.licenses.length) {
        return;
      }
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await this.$store.dispatch("license/getLicenseList", {
          license_name: "",
          license_phone_number: "",
          license_status: "",
          page: 0,
          perPage: 1000
        });
        const payload = response && response.data ? response.data : {};
        this.licenses = Array.isArray(payload.data) ? payload.data : [];
      } catch (error) {
        this.licenses = [];
        this.errorMessage = getApiErrorMessage(
          error,
          "Informasi masa aktif lisensi belum dapat dimuat."
        );
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.waajo-renewal {
  margin-bottom: 22px;
  padding: 20px 22px 10px;
  border: 1px solid #f1e4d4;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.045);
}

.waajo-renewal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f2ede6;
}

.waajo-renewal-heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.waajo-renewal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  background: #fff3e6;
  color: #e88931;
  font-size: 16px;
}

.waajo-renewal-heading h2 {
  margin: 0;
  color: #172033;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.waajo-renewal-heading p {
  margin: 4px 0 0;
  color: #7b8798;
  font-size: 11px;
  line-height: 1.55;
}

.waajo-renewal-all {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  margin-top: 3px;
  color: #d97b22;
  font-size: 11px;
  font-weight: 800;
  text-decoration: none;
}

.waajo-renewal-all:hover,
.waajo-renewal-all:focus {
  color: #b45309;
  text-decoration: none;
  outline: none;
}

.waajo-renewal-list {
  display: grid;
  gap: 0;
}

.waajo-renewal-row {
  display: grid;
  grid-template-columns: 36px minmax(160px, 1.1fr) minmax(170px, 1fr) minmax(145px, 0.85fr) auto;
  align-items: center;
  gap: 14px;
  padding: 14px 2px;
  border-bottom: 1px solid #f7f2ec;
}

.waajo-renewal-row:last-child {
  border-bottom: 0;
}

.waajo-renewal-wa {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #ecfdf3;
  color: #25a85a;
  font-size: 18px;
}

.waajo-renewal-info {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.waajo-renewal-info strong {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #172033;
  font-size: 13px;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.waajo-renewal-info > span {
  color: #8290a2;
  font-size: 11px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.waajo-renewal-active-tag {
  padding: 2px 6px;
  border-radius: 999px;
  background: #fff3e6;
  color: #b45309;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.waajo-renewal-status {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.waajo-renewal-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  max-width: 100%;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 800;
  line-height: 1.4;
}

.waajo-renewal-row.is-warning .waajo-renewal-badge {
  background: #fff7e8;
  color: #c26b16;
}

.waajo-renewal-row.is-expired .waajo-renewal-badge {
  background: #fff0f0;
  color: #c93d3d;
}

.waajo-renewal-status small {
  color: #8b97a8;
  font-size: 10px;
  line-height: 1.4;
}

.waajo-renewal-remaining {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.waajo-renewal-remaining strong {
  color: #475569;
  font-size: 11px;
  font-weight: 800;
}

.waajo-renewal-row.is-expired .waajo-renewal-remaining strong {
  color: #b91c1c;
}

.waajo-renewal-remaining span {
  color: #9aa5b4;
  font-size: 10px;
  line-height: 1.4;
}

.waajo-renewal-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 34px;
  padding: 8px 13px;
  border-radius: 9px;
  background: #f99a43;
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 6px 14px rgba(249, 154, 67, 0.18);
}

.waajo-renewal-action.is-expired {
  background: #e76a56;
  box-shadow: 0 6px 14px rgba(231, 106, 86, 0.16);
}

.waajo-renewal-action:hover,
.waajo-renewal-action:focus {
  color: #ffffff;
  text-decoration: none;
  outline: none;
  filter: brightness(0.96);
}

.waajo-renewal-loading {
  display: grid;
  gap: 2px;
}

.waajo-renewal-skeleton {
  display: grid;
  grid-template-columns: 36px minmax(150px, 1fr) minmax(160px, 1fr) auto;
  align-items: center;
  gap: 14px;
  padding: 14px 2px;
}

.waajo-skeleton-circle,
.waajo-skeleton-lines span,
.waajo-skeleton-button {
  display: block;
  border-radius: 8px;
  background: linear-gradient(90deg, #f5f7fa 25%, #eef2f6 50%, #f5f7fa 75%);
  background-size: 200% 100%;
  animation: waajo-skeleton-pulse 1.4s infinite;
}

.waajo-skeleton-circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
}

.waajo-skeleton-lines {
  display: grid;
  gap: 6px;
}

.waajo-skeleton-lines span:first-child {
  width: 45%;
  height: 10px;
}

.waajo-skeleton-lines span:last-child {
  width: 68%;
  height: 8px;
}

.waajo-skeleton-button {
  width: 92px;
  height: 32px;
}

@keyframes waajo-skeleton-pulse {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.waajo-renewal-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 13px 0 9px;
  color: #b91c1c;
  font-size: 11px;
  line-height: 1.5;
}

.waajo-renewal-error > span {
  flex: 1 1 auto;
}

.waajo-renewal-retry {
  flex: 0 0 auto;
  padding: 5px 9px;
  border: 1px solid #fecaca;
  border-radius: 7px;
  background: #fff7f7;
  color: #b91c1c;
  font-size: 10px;
  font-weight: 800;
  cursor: pointer;
}

.waajo-renewal-safe {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 14px 0 9px;
  color: #64748b;
  font-size: 11px;
}

.waajo-renewal-safe-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ecfdf3;
  color: #15803d;
  font-size: 11px;
}

.waajo-renewal-note {
  margin: 0;
  padding: 8px 0 2px;
  color: #9aa5b4;
  font-size: 10px;
  line-height: 1.5;
}

@media (max-width: 991.98px) {
  .waajo-renewal-row {
    grid-template-columns: 36px minmax(0, 1fr) auto;
  }

  .waajo-renewal-status,
  .waajo-renewal-remaining {
    grid-column: 2 / 3;
  }

  .waajo-renewal-action {
    grid-column: 3 / 4;
    grid-row: 1 / 3;
  }
}

@media (max-width: 640px) {
  .waajo-renewal {
    padding: 17px 15px 8px;
  }

  .waajo-renewal-head {
    flex-direction: column;
    gap: 8px;
  }

  .waajo-renewal-row {
    grid-template-columns: 34px minmax(0, 1fr);
    gap: 10px 12px;
    padding: 13px 0;
  }

  .waajo-renewal-status,
  .waajo-renewal-remaining {
    grid-column: 2 / 3;
  }

  .waajo-renewal-action {
    grid-column: 1 / -1;
    grid-row: auto;
    width: 100%;
    margin-top: 2px;
  }

  .waajo-renewal-skeleton {
    grid-template-columns: 34px minmax(0, 1fr);
  }

  .waajo-skeleton-button {
    grid-column: 2 / 3;
    width: 100px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/dashboard/components/LicenseRenewal.vue